import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AiFillClockCircle, AiOutlineArrowRight } from "react-icons/ai";
import { BiSolidMessageAlt } from "react-icons/bi";

interface EduFeed {
  id: number;
  name: string;
  avatar: string;
  description: string;
  tag: string;
}

/* Sample education posts so the section never appears empty,
   even when the backend is not running. */
const dummyFeeds: EduFeed[] = [
  {
    id: 1,
    name: "Courses that will actually boost your career this year",
    avatar: "/blog/education.png",
    description:
      "From data analytics to UX design, these in-demand skills are opening doors to high-paying roles across every major industry.",
    tag: "Career",
  },
  {
    id: 2,
    name: "How to build a study routine that really sticks",
    avatar: "/blog/edu-latest.png",
    description:
      "Consistency beats intensity. Discover a sustainable daily schedule with focus techniques that keep you on track all semester.",
    tag: "Study Tips",
  },
  {
    id: 3,
    name: "AI tutors: the classroom revolution already happening",
    avatar:
      "https://images.unsplash.com/photo-1610484826967-09c5720778c7?auto=format&fit=crop&w=1200&q=80",
    description:
      "Personalised AI assistance is adapting lessons to every student's pace, making quality education more accessible than ever before.",
    tag: "EdTech",
  },
  {
    id: 4,
    name: "Government scholarships: your complete application guide",
    avatar:
      "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&w=1200&q=80",
    description:
      "A round-up of the most generous funded programmes plus tips for writing references that make your application stand out.",
    tag: "Scholarship",
  },
];

const API_BASE = "https://localhost:7246/";

/* Resolve API-relative paths to absolute, while keeping plain asset paths. */
const toImage = (src: string) =>
  src.startsWith("http") || src.startsWith("/") ? src : API_BASE + src;

export default function Education() {
  const [feeds, setFeeds] = useState<EduFeed[]>(dummyFeeds);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_BASE}api/Feeds`);
        const data = await response.json();
        if (data && data.result?.length) {
          setFeeds(data.result);
        }
      } catch (error) {
        // Keep the dummy data when the backend is unreachable.
        console.warn("Education API unavailable, showing sample posts:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="pt-10 pb-16">
      <div className="bg-white py-5 md:px-12 px-4 flex justify-between items-center rounded-lg shadow-sm">
        <h2 className="font-semibold capitalize text-xl md:text-3xl relative">
          <span>Educations</span>
          <div className="absolute -bottom-4 left-0 h-[6px] w-full bg-secondary"></div>
        </h2>
        <p className="text-primary text-xs md:text-base hover:underline cursor-pointer">
          View more Articles
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-8">
        {feeds.map((feed) => (
          <Link
            to="/blog-details"
            key={feed.id}
            className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col"
          >
            <div className="relative h-48 md:h-56 overflow-hidden">
              <img
                className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                src={toImage(feed.avatar)}
                alt={`avatar-${feed.id}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
              <span className="absolute top-4 left-4 px-3 py-1 text-xs font-semibold rounded-full bg-secondary text-white shadow">
                {feed.tag}
              </span>
            </div>
            <div className="p-6 flex flex-col gap-3 flex-1">
              <h2 className="font-semibold text-lg md:text-xl leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                {feed.name}
              </h2>
              <p className="text-sm text-gray-600 line-clamp-2">{feed.description}</p>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1.5">
                  <AiFillClockCircle /> 1 week ago
                </span>
                <span className="flex items-center gap-1.5">
                  <BiSolidMessageAlt /> 67
                </span>
              </div>
            </div>
            <span className="px-6 pb-6 inline-flex items-center gap-2 text-sm font-semibold text-primary">
              Continue Reading
              <AiOutlineArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
